import { landingPageContent } from '@content/landing-page'
import { ChevronRight } from 'lucide-react'
import { motion, useInView } from 'motion/react'
import { useRef } from 'react'

import { BorderBeam } from '@components/magicui/border-beam'
import { cn } from '@utils/cn'

export interface HeroProps {
  images: {
    hero: string
  }
  callToActionLink?: string
}

export function Hero({ images, callToActionLink }: HeroProps) {
  const fadeInRef = useRef(null)
  const fadeInInView = useInView(fadeInRef, {
    once: true
  })

  const fadeUpVariants = {
    initial: {
      opacity: 0,
      y: 24
    },
    animate: {
      opacity: 1,
      y: 0
    }
  }

  return (
    <section id='hero'>
      <div className='relative h-[calc(100vh-100px)] overflow-hidden sm:h-[calc(100vh+25rem)]'>
        <div className='z-10 container mx-auto flex flex-col'>
          <div className='mt-20 grid grid-cols-1'>
            <div className='flex flex-col items-center gap-6 pb-8 text-center'>
              <motion.h1
                ref={fadeInRef}
                className='bg-gradient-to-br from-black from-[30%] to-black/60 bg-clip-text py-6 text-5xl leading-none font-medium tracking-tighter text-balance text-transparent sm:text-6xl md:text-7xl lg:text-8xl dark:from-white dark:to-white/40'
                animate={fadeInInView ? 'animate' : 'initial'}
                variants={fadeUpVariants}
                initial={false}
                transition={{
                  duration: 0.6,
                  delay: 0.1,
                  ease: [0.21, 0.47, 0.32, 0.98],
                  type: 'spring'
                }}
              >
                {landingPageContent.hero.title[0]}
                <br />
                {landingPageContent.hero.title[1]}
                <br />
              </motion.h1>
              <motion.p
                className='text-lg tracking-tight text-balance text-gray-400 md:text-xl'
                animate={fadeInInView ? 'animate' : 'initial'}
                variants={fadeUpVariants}
                initial={false}
                transition={{
                  duration: 0.6,
                  delay: 0.2,
                  ease: [0.21, 0.47, 0.32, 0.98],
                  type: 'spring'
                }}
              >
                {landingPageContent.hero.description}
              </motion.p>

              {callToActionLink && (
                <motion.div
                  animate={fadeInInView ? 'animate' : 'initial'}
                  variants={fadeUpVariants}
                  className='flex flex-col gap-4 lg:flex-row'
                  initial={false}
                  transition={{
                    duration: 0.6,
                    delay: 0.3,
                    ease: [0.21, 0.47, 0.32, 0.98],
                    type: 'spring'
                  }}
                >
                  <a
                    href={callToActionLink}
                    target='_blank'
                    rel='noopener noreferrer nofollow'
                    className={cn(
                      // colors
                      'bg-black text-white shadow hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90',

                      // layout
                      'group focus-visible:ring-ring relative inline-flex h-9 w-full items-center justify-center gap-2 overflow-hidden rounded-md px-4 py-2 text-base font-semibold tracking-tighter whitespace-pre focus-visible:ring-1 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 md:flex',

                      // animation
                      'hover:ring-primary transform-gpu ring-offset-current transition-all duration-300 ease-out hover:ring-2 hover:ring-offset-2'
                    )}
                  >
                    {landingPageContent.callToAction.buttonText}
                    <ChevronRight className='size-4 translate-x-0 transition-all duration-300 ease-out group-hover:translate-x-1' />
                  </a>
                </motion.div>
              )}
            </div>
          </div>

          <motion.div
            animate={fadeInInView ? 'animate' : 'initial'}
            variants={fadeUpVariants}
            initial={false}
            transition={{
              duration: 1.4,
              delay: 0.4,
              ease: [0.21, 0.47, 0.32, 0.98],
              type: 'spring'
            }}
            className='relative mt-24 h-full w-full rounded-xl after:pointer-events-none after:absolute after:inset-0 after:z-10 after:content-[""] after:[background:linear-gradient(to_top,#fff_30%,transparent)] dark:after:[background:linear-gradient(to_top,#000000_30%,transparent)]'
          >
            <div
              className={cn(
                'absolute inset-0 bottom-1/2 h-full w-full transform-gpu [filter:blur(120px)]',

                // light styles
                '[background-image:linear-gradient(to_bottom,#FF5655,transparent_30%)]',

                // dark styles
                'dark:[background-image:linear-gradient(to_bottom,#ffffff,transparent_30%)]'
              )}
            />

            <img
              src={images.hero}
              alt='Akadenia Shopify Upload Hero'
              decoding='async'
              className='relative block h-full w-full rounded-xl border'
            />
            {/* <video
              autoPlay
              loop
              muted
              src="demo.mp4"
              className="h-auto w-full"
            /> */}

            <BorderBeam size={150} />
            <BorderBeam size={150} delay={7} />
          </motion.div>
        </div>
      </div>
    </section>
  )
}
